import React, { memo, useEffect, useState } from "react";
import { withNamespaces } from "react-i18next";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Button,
  Card,
  CardBody,
  Col,
  FormGroup,
  Input,
  Label,
  Row,
} from "reactstrap";
import { toast } from "react-toastify";
import Select from "react-select";
import { TbLoader } from "react-icons/tb";
import GetHelpFromAIIcon from "src/modules/data-protection/assets/images/microchip/black.png";
import DateUtils from "src/services/utils/DateUtils";
import { INTERNATIONAL_DATE_TIME_FORMAT } from "src/common/constants";
import ReportIncidenService from "../../components/api/ReportIncidenService";
import getApiErrorMessage from "../../utils/getApiErrorMessage";
import mergeLiaGenerateResult from "../../utils/mergeLiaGenerateResult";
import LiaAiConfirmationModal from "./components/LiaAiConfirmationModal";
import LiaUserResponseConfirmationModal from "./components/LiaUserResponseConfirmationModal";
import UserResponsePreview from "./components/UserResponsePreview";
import LegitimationSection from "../../components/legitimation/LegitimationSection";
import { useDsrDeadlineWorkflow } from "../deadline-management/hooks/useDsrDeadlineWorkflow";
import AwaitingRequesterBanner from "../deadline-management/components/awaiting-requester-banner";

const SUBJECT_RELATIONSHIPS = [
  "customer",
  "supplier",
  "visitor",
  "employee",
  "other",
];

const LAWFUL_BASES = [
  "legal_obligation",
  "legitimate_interest",
  "consent",
  "contract",
  "public_task",
  "vital_interests",
];

const getSelectOption = (options, value) =>
  options.find((option) => option.value === value) || null;

const LAWFUL_BASIS_LABELS = {
  legal_obligation: "Legal Obligation",
  legitimate_interest: "Legitimate Interest",
  consent: "Consent",
  contract: "Contract",
  public_task: "Public Task",
  vital_interests: "Vital Interests",
};

const LiaTab = ({ t, caseId, report, reportRefetch, onCopyToMessages }) => {
  const dateUtils = new DateUtils();
  const isDeleted = report?.status === 5 || report?.is_deleted_content;
  const isReadOnly = report?.is_read_only_analyst || isDeleted;

  const [form, setForm] = useState({});
  const [isGenerateLiaModalOpen, setIsGenerateLiaModalOpen] = useState(false);
  const [isGenerateUserResponseModalOpen, setIsGenerateUserResponseModalOpen] =
    useState(false);
  const [responseSubject, setResponseSubject] = useState("");
  const [verificationSteps, setVerificationSteps] = useState([]);
  const [retentionNotes, setRetentionNotes] = useState("");
  const [legitimationNotesError, setLegitimationNotesError] = useState("");

  const workflowQuery = useQuery({
    queryKey: ["dsr-workflow", caseId],
    queryFn: async () => {
      const service = ReportIncidenService.getInstance();
      return await service.fetchDsrWorkflow(caseId);
    },
    cacheTime: 0,
    refetchOnWindowFocus: false,
    enabled: !!caseId,
    onError: () => {
      toast(t("An error occurred while fetching LIA workflow."), {
        type: "error",
      });
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (payload) => {
      const service = ReportIncidenService.getInstance();
      return await service.updateDsrWorkflow(caseId, payload);
    },
    onSuccess: (data) => {
      setForm(data);
      toast(t("Workflow saved successfully."), { type: "success" });
      reportRefetch?.();
    },
    onError: () => {
      toast(t("Failed to save workflow."), { type: "error" });
    },
  });

  const generateLiaMutation = useMutation({
    mutationFn: async () => {
      const service = ReportIncidenService.getInstance();
      return await service.generateDsrLia(caseId);
    },
    onSuccess: (data) => {
      setForm((prev) => mergeLiaGenerateResult(prev, data));
      if (data?.ai?.lawful_basis_recommendation) {
        toast(
          `${t("AI recommendation")}: ${data.ai.lawful_basis_recommendation}`,
          { type: "info" }
        );
      }
      toast(t("LIA generated successfully. Please review and save."), {
        type: "success",
      });
    },
    onError: (error) => {
      toast(
        t(getApiErrorMessage(error, t("Failed to generate LIA."))),
        { type: "error" }
      );
    },
    onSettled: () => {
      setIsGenerateLiaModalOpen(false);
    },
  });

  const generateUserResponseMutation = useMutation({
    mutationFn: async () => {
      const service = ReportIncidenService.getInstance();
      return await service.generateDsrResponse(caseId, {
        target: "user_message",
      });
    },
    onSuccess: (data) => {
      setForm((prev) => {
        const next = data?.workflow ? { ...prev, ...data.workflow } : { ...prev };
        if (data?.body_html) {
          next.user_response_draft = data.body_html;
        }
        return next;
      });
      setResponseSubject(data?.subject || "");
      setVerificationSteps(data?.verification_steps || []);
      setRetentionNotes(data?.retention_notes || "");
      toast(t("Response generated successfully. Please review before copying to Messages."), {
        type: "success",
      });
    },
    onError: (error) => {
      toast(
        t(getApiErrorMessage(error, t("Failed to generate response."))),
        { type: "error" }
      );
    },
    onSettled: () => {
      setIsGenerateUserResponseModalOpen(false);
    },
  });

  useEffect(() => {
    if (workflowQuery.data) {
      setForm(workflowQuery.data);
    }
  }, [workflowQuery.data]);

  const handleFieldChange = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };

      if (field === "lawful_basis") {
        next.lia_required = value === "legitimate_interest";
      }

      if (field === "legitimation_notes" && value?.trim()) {
        setLegitimationNotesError("");
      }

      if (
        field === "legitimation_status" &&
        value !== "failed"
      ) {
        setLegitimationNotesError("");
      }

      return next;
    });
  };

  const handleSave = () => {
    if (
      form.legitimation_status === "failed" &&
      !form.legitimation_notes?.trim()
    ) {
      const message = t("Please document why verification failed.");
      setLegitimationNotesError(message);
      toast(message, { type: "error" });
      return;
    }

    setLegitimationNotesError("");
    saveMutation.mutate({
      subject_relationship: form.subject_relationship,
      lawful_basis: form.lawful_basis,
      lia_purpose: form.lia_purpose,
      lia_necessity: form.lia_necessity,
      lia_balancing: form.lia_balancing,
      lia_summary: form.lia_summary,
      legitimation_status: form.legitimation_status,
      legitimation_notes: form.legitimation_notes,
      data_access_instructions: form.data_access_instructions,
    });
  };

  const deadlineWorkflow = useDsrDeadlineWorkflow(caseId);
  const daysUntilDeadline = deadlineWorkflow.isDeadlineManagementEnabled
    && deadlineWorkflow.data?.days_until_effective_deadline != null
    ? deadlineWorkflow.data.days_until_effective_deadline
    : report?.days_until_deadline ?? form.days_until_deadline;
  const deadlineAt = deadlineWorkflow.isDeadlineManagementEnabled
    ? deadlineWorkflow.data?.effective_deadline_at ||
      report?.dsr_deadline_at ||
      form.dsr_deadline_at
    : report?.dsr_deadline_at ?? form.dsr_deadline_at;
  const savedLawfulBasis = workflowQuery.data?.lawful_basis;
  const showLiaSection = form.lawful_basis === "legitimate_interest";
  const isLawfulBasisSavedAsLegitimateInterest =
    savedLawfulBasis === "legitimate_interest";
  const needsSaveBeforeGenerateLia =
    form.lawful_basis === "legitimate_interest" &&
    !isLawfulBasisSavedAsLegitimateInterest;
  const canGenerateLia =
    showLiaSection &&
    form.lawful_basis === "legitimate_interest" &&
    !isReadOnly;
  const canGenerateUserResponse = !isReadOnly;
  const showLegitimationWarning =
    form.legitimation_status === "pending" ||
    form.legitimation_status === "failed";
  const displayVerificationSteps = ["pending", "requested"].includes(
    form.legitimation_status
  )
    ? verificationSteps
    : [];

  const getDeadlineState = () => {
    if (daysUntilDeadline < 0) return "overdue";
    if (daysUntilDeadline <= 5) return "warning";
    return "info";
  };

  const getDeadlineCountdownLabel = () => {
    if (daysUntilDeadline == null) return "";
    if (daysUntilDeadline < 0) {
      return `${Math.abs(daysUntilDeadline)} ${t("days overdue")}`;
    }
    return `${daysUntilDeadline} ${t("days remaining")}`;
  };

  const handleOpenGenerateLiaModal = () => {
    if (needsSaveBeforeGenerateLia) {
      toast(t("Save before generating LIA."), { type: "warning" });
      return;
    }
    setIsGenerateLiaModalOpen(true);
  };

  const handleCopyToMessages = () => {
    if (!form.user_response_draft?.trim()) {
      return;
    }
    onCopyToMessages?.(form.user_response_draft);
  };

  const formatDateTime = (value) => {
    if (!value) return "—";
    return dateUtils.convertDateToDate(value, INTERNATIONAL_DATE_TIME_FORMAT);
  };

  const subjectRelationshipOptions = SUBJECT_RELATIONSHIPS.map((value) => ({
    value,
    label: t(value.charAt(0).toUpperCase() + value.slice(1)),
  }));

  const lawfulBasisOptions = LAWFUL_BASES.map((value) => ({
    value,
    label: t(LAWFUL_BASIS_LABELS[value]),
  }));

  if (workflowQuery.isLoading) {
    return (
      <div
        className="dt-field dt-skeleton"
        style={{ width: "100%", height: "320px" }}
      />
    );
  }

  return (
    <div className="lia-tab">
      <AwaitingRequesterBanner caseId={caseId} report={report} />
      <LiaAiConfirmationModal
        t={t}
        isOpen={isGenerateLiaModalOpen}
        toggle={() => setIsGenerateLiaModalOpen(false)}
        onConfirm={() => generateLiaMutation.mutate()}
        isLoading={generateLiaMutation.isLoading}
      />

      <LiaUserResponseConfirmationModal
        t={t}
        isOpen={isGenerateUserResponseModalOpen}
        toggle={() => setIsGenerateUserResponseModalOpen(false)}
        onConfirm={() => generateUserResponseMutation.mutate()}
        isLoading={generateUserResponseMutation.isLoading}
        showLegitimationWarning={showLegitimationWarning}
      />

      {deadlineAt != null && (
        <div className={`dsr-deadline-banner dsr-deadline-banner--${getDeadlineState()}`}>
          <div className="dsr-deadline-banner__left">
            <span className="dsr-deadline-banner__icon">
              !
            </span>
            <div className="dsr-deadline-banner__text">
              <strong>{t("GDPR Response Deadline")}</strong>
              <span>{formatDateTime(deadlineAt)}</span>
            </div>
          </div>
          {daysUntilDeadline != null && (
            <span className="dsr-deadline-banner__pill">
              {getDeadlineCountdownLabel()}
            </span>
          )}
        </div>
      )}

      <Row className="lia-tab__top-row g-3">
        <Col lg="6" className="d-flex">
          <Card className="lia-tab__section-card flex-fill">
            <CardBody>
              <h6 className="lia-tab__section-title">{t("Case Information")}</h6>
              <FormGroup>
                <Label for="subject_relationship">{t("Subject Relationship")}</Label>
                <Select
                  inputId="subject_relationship"
                  classNamePrefix="select2-selection"
                  placeholder={`${t("Select")}...`}
                  options={subjectRelationshipOptions}
                  value={getSelectOption(
                    subjectRelationshipOptions,
                    form.subject_relationship
                  )}
                  isDisabled={isReadOnly}
                  menuPortalTarget={document.body}
                  onChange={(option) =>
                    handleFieldChange("subject_relationship", option?.value || "")
                  }
                />
              </FormGroup>

              <FormGroup className="mb-0">
                <Label for="lawful_basis">{t("Lawful Basis")}</Label>
                <Select
                  inputId="lawful_basis"
                  classNamePrefix="select2-selection"
                  placeholder={`${t("Select")}...`}
                  options={lawfulBasisOptions}
                  value={getSelectOption(lawfulBasisOptions, form.lawful_basis)}
                  isDisabled={isReadOnly}
                  menuPortalTarget={document.body}
                  onChange={(option) =>
                    handleFieldChange("lawful_basis", option?.value || "")
                  }
                />
              </FormGroup>
            </CardBody>
          </Card>
        </Col>

        <Col lg="6" className="d-flex">
          <Card className="lia-tab__section-card flex-fill">
            <CardBody>
              <LegitimationSection
                variant="card"
                form={form}
                isReadOnly={isReadOnly}
                onFieldChange={handleFieldChange}
                formatDateTime={formatDateTime}
                notesError={legitimationNotesError}
                requesterType={report?.requester_type || form?.requester_type}
              />
            </CardBody>
          </Card>
        </Col>
      </Row>

      {!showLiaSection && form.lawful_basis && (
        <div className="dsr-deadline-banner dsr-deadline-banner--info">
          <div className="dsr-deadline-banner__left">
            <span className="dsr-deadline-banner__icon">!</span>
            <span className="dsr-lia-info-banner__message">
              {t("LIA is not required for this lawful basis.")}
            </span>
          </div>
        </div>
      )}

      {showLiaSection && (
        <Card className="lia-tab__section-card">
          <CardBody>
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h6 className="lia-tab__section-title mb-0">
                {t("LIA Assessment")}
              </h6>
              {canGenerateLia && (
                <Button
                  color="primary"
                  outline
                  className="ai-base-risk-assessment-button text-nowrap"
                  onClick={handleOpenGenerateLiaModal}
                  disabled={generateLiaMutation.isLoading}
                >
                  {generateLiaMutation.isLoading ? (
                    <TbLoader className="spin-animation" />
                  ) : (
                    <>
                      <img
                        alt="ai icon"
                        src={GetHelpFromAIIcon}
                        style={{
                          width: 18,
                          height: 18,
                          marginRight: 10,
                          marginTop: -2,
                        }}
                      />
                      {t("Generate LIA")}
                    </>
                  )}
                </Button>
              )}
            </div>

            <FormGroup>
              <Label for="lia_purpose">{t("Purpose")}</Label>
              <Input
                type="textarea"
                id="lia_purpose"
                className="lia-tab__textarea"
                rows="4"
                value={form.lia_purpose || ""}
                disabled={isReadOnly}
                onChange={(e) => handleFieldChange("lia_purpose", e.target.value)}
              />
            </FormGroup>

            <FormGroup>
              <Label for="lia_necessity">{t("Necessity")}</Label>
              <Input
                type="textarea"
                id="lia_necessity"
                className="lia-tab__textarea"
                rows="4"
                value={form.lia_necessity || ""}
                disabled={isReadOnly}
                onChange={(e) =>
                  handleFieldChange("lia_necessity", e.target.value)
                }
              />
            </FormGroup>

            <FormGroup>
              <Label for="lia_balancing">{t("Balancing")}</Label>
              <Input
                type="textarea"
                id="lia_balancing"
                className="lia-tab__textarea"
                rows="4"
                value={form.lia_balancing || ""}
                disabled={isReadOnly}
                onChange={(e) =>
                  handleFieldChange("lia_balancing", e.target.value)
                }
              />
            </FormGroup>

            <FormGroup className="mb-0">
              <Label for="lia_summary">{t("Summary")}</Label>
              <Input
                type="textarea"
                id="lia_summary"
                className="lia-tab__textarea"
                rows="4"
                value={form.lia_summary || ""}
                disabled={isReadOnly}
                onChange={(e) => handleFieldChange("lia_summary", e.target.value)}
              />
            </FormGroup>
          </CardBody>
        </Card>
      )}

      <Card className="lia-tab__section-card">
        <CardBody>
          <h6 className="lia-tab__section-title">{t("Data Access Instruction")}</h6>
          <FormGroup className="mb-0">
            <Label for="data_access_instructions" className="visually-hidden">
              {t("Data Access Instruction")}
            </Label>
            <Input
              type="textarea"
              id="data_access_instructions"
              className="lia-tab__textarea"
              rows="3"
              placeholder={t("Enter data access instructions...")}
              value={form.data_access_instructions || ""}
              disabled={isReadOnly}
              onChange={(e) =>
                handleFieldChange("data_access_instructions", e.target.value)
              }
            />
          </FormGroup>
        </CardBody>
      </Card>

      <Card className="lia-tab__section-card">
        <CardBody>
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h6 className="lia-tab__section-title mb-0">
              {t("Response to Data Subject")}
            </h6>
            {canGenerateUserResponse && (
              <Button
                color="primary"
                outline
                className="ai-base-risk-assessment-button text-nowrap"
                onClick={() => setIsGenerateUserResponseModalOpen(true)}
                disabled={generateUserResponseMutation.isLoading}
              >
                {generateUserResponseMutation.isLoading ? (
                  <TbLoader className="spin-animation" />
                ) : (
                  <>
                    <img
                      alt="ai icon"
                      src={GetHelpFromAIIcon}
                      style={{
                        width: 18,
                        height: 18,
                        marginRight: 10,
                        marginTop: -2,
                      }}
                    />
                    {t("Generate Response")}
                  </>
                )}
              </Button>
            )}
          </div>

          <UserResponsePreview
            subject={responseSubject}
            bodyHtml={form.user_response_draft}
            verificationSteps={displayVerificationSteps}
            retentionNotes={retentionNotes}
            isReadOnly={isReadOnly}
            onCopyToMessages={handleCopyToMessages}
            copyDisabled={!form.user_response_draft?.trim() || !onCopyToMessages}
          />
        </CardBody>
      </Card>

      {!isReadOnly && (
        <div className="d-flex justify-content-end">
          <Button
            color="primary"
            onClick={handleSave}
            disabled={saveMutation.isLoading}
          >
            {saveMutation.isLoading ? (
              <TbLoader className="spin-animation" />
            ) : (
              t("Save")
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export default withNamespaces()(memo(LiaTab));
